import React, { FormEvent, ReactNode } from 'react';
import { Button, Typography, styled } from '@mui/material';
import { Close } from '@mui/icons-material';

// Acima da sidebar (1200) e da appbar (1102); abaixo de modais e popovers (1300),
// para que listas de autocomplete e seletores de data continuem visíveis.
const FORM_PAGE_Z_INDEX = 1250;

const Root = styled('div')(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: FORM_PAGE_Z_INDEX,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: theme.palette.background.default,

  // Campos do formato padrão: rótulo acima (FormField), fundo suave e altura compacta.
  '& .MuiOutlinedInput-root': {
    background: '#fafafa',
    fontSize: 13,
    '&.Mui-disabled': {
      background: '#f0f0f0',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#e2e2e2',
  },
  '& .MuiOutlinedInput-root input, & .MuiOutlinedInput-root textarea:not([aria-hidden]), & .MuiSelect-select.MuiSelect-outlined':
    {
      paddingTop: '8px !important',
      paddingBottom: '8px !important',
      fontSize: 13,
    },
  '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
    padding: '0 !important',
  },
  '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
    minHeight: '36px !important',
    background: '#fafafa',
    fontSize: 13,
  },
  '& .MuiPickersOutlinedInput-notchedOutline': {
    borderColor: '#e2e2e2',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 2,
    fontSize: 10,
  },
  '& .MuiInputAdornment-root p': {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  '& ::placeholder': {
    opacity: 0.6,
  },
}));

const Header = styled('header')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  minHeight: 56,
  padding: theme.spacing(1, 3.75),
  background: '#fff',
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(1, 2),
  },
}));

const Title = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  minWidth: 0,
  '& .brand': {
    color: theme.palette.primary.main,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  '& .divider': {
    width: 1,
    height: 20,
    background: theme.palette.divider,
  },
  '& .page-title': {
    color: theme.palette.text.secondary,
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
    '& .divider': {
      display: 'none',
    },
  },
}));

const Body = styled('form')(({ theme }) => ({
  width: '100%',
  maxWidth: 1180,
  margin: '0 auto',
  padding: theme.spacing(3.5, 3.75),
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 280px',
  columnGap: theme.spacing(3),
  alignItems: 'start',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
    padding: theme.spacing(2),
    rowGap: theme.spacing(2),
  },
}));

const Main = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 0,
}));

const Aside = styled('aside')(({ theme }) => ({
  position: 'sticky',
  top: 56 + 28,
  [theme.breakpoints.down('md')]: {
    position: 'static',
  },
}));

interface FormPageProps {
  title: string;
  onClose(): void;
  onSubmit(e: FormEvent<HTMLFormElement>): void;
  aside?: ReactNode;
  children: ReactNode;
}

const FormPage: React.FC<FormPageProps> = ({ title, onClose, onSubmit, aside, children }) => {
  return (
    <Root>
      <Header>
        <Title>
          <Typography className="brand">Universo Plástica</Typography>
          <span className="divider" />
          <Typography className="page-title">{title}</Typography>
        </Title>

        <Button color="inherit" size="small" endIcon={<Close fontSize="small" />} onClick={onClose}>
          Fechar
        </Button>
      </Header>

      <Body onSubmit={onSubmit} noValidate>
        <Main>{children}</Main>
        {aside && <Aside>{aside}</Aside>}
      </Body>
    </Root>
  );
};

export default FormPage;
